/**
 * Normalize `axios` `error.response.data` to a plain object when possible.
 * Handles JSON string bodies and common `{ data: { message, toast } }` wrappers
 * so mutation errors (e.g. `OBJECT_VALIDATION_RULE` + `toast`) are visible to the UI.
 *
 * @param {unknown} error - Axios error or similar
 * @returns {Record<string, unknown>|null}
 */
export function getApiErrorResponseData(error) {
  const raw = error?.response?.data;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;

  const looksLikeErrorPayload = o =>
    o &&
    typeof o === 'object' &&
    !Array.isArray(o) &&
    (o.toast != null ||
      o.code != null ||
      typeof o.message === 'string' ||
      typeof o.error === 'string' ||
      o.success === false ||
      o.success === true);

  if (looksLikeErrorPayload(raw)) return raw;
  if (
    raw.data &&
    typeof raw.data === 'object' &&
    !Array.isArray(raw.data) &&
    looksLikeErrorPayload(raw.data)
  ) {
    return raw.data;
  }
  return raw;
}

function normalizeApiToastFontSize(raw) {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw))
    return `${Math.round(raw)}px`;
  const s = String(raw).trim();
  if (!s) return undefined;
  if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
  return s;
}

/**
 * Maps `error.response.data.toast` (e.g. OBJECT_VALIDATION_RULE) to AppToast props.
 *
 * @param {unknown} apiToast
 * @returns {{ sticky: boolean, delay?: number, messageFontSize?: string } | null} `null` if no toast object
 */
export function parseApiToastHints(apiToast) {
  if (!apiToast || typeof apiToast !== 'object' || Array.isArray(apiToast)) {
    return null;
  }

  let delayMs;
  const rawDelay = apiToast.delay;
  if (rawDelay != null && rawDelay !== '') {
    const n = Number(rawDelay);
    if (Number.isFinite(n)) {
      delayMs = Math.max(0, Math.min(120000, Math.floor(n)));
    }
  }

  const durationZero = delayMs === 0;
  const sticky =
    apiToast.sticky === true ||
    apiToast.sticky === 1 ||
    String(apiToast.sticky).toLowerCase() === 'true' ||
    durationZero;

  const messageFontSize = normalizeApiToastFontSize(apiToast.fontSize);

  /** @type {{ sticky: boolean, delay?: number, messageFontSize?: string }} */
  const out = { sticky };
  if (!sticky && delayMs != null && delayMs > 0) {
    out.delay = delayMs;
  }
  if (messageFontSize) {
    out.messageFontSize = messageFontSize;
  }
  return out;
}

/**
 * Pull a human-readable message from axios/API errors. Handles string bodies,
 * `{ message }`, `{ error }`, and Express-style `{ errors }`.
 */
function extractServerMessage(error) {
  const data = getApiErrorResponseData(error) ?? error?.response?.data;
  if (data == null) return null;
  if (typeof data === 'string') {
    const t = data.trim();
    return t || null;
  }
  if (typeof data === 'object' && data !== null) {
    return (
      data.message ||
      data.error ||
      (typeof data.errors === 'string' ? data.errors : null) ||
      null
    );
  }
  return null;
}

function isGenericAxiosStatusMessage(msg) {
  return (
    typeof msg === 'string' &&
    /^Request failed with status code \d+$/.test(msg.trim())
  );
}

const DEFAULT_FORBIDDEN =
  "You don't have permission to view or edit this record.";

/**
 * User-facing API error text. For HTTP 403, uses a clear permission message when
 * the server sent nothing useful or only Axios’s generic status string.
 *
 * @param {unknown} error - Axios error or similar
 * @param {string} [fallback='An unexpected error occurred.'] - Non-403 default
 */
export function getApiErrorMessage(
  error,
  fallback = 'An unexpected error occurred.'
) {
  const status = error?.response?.status;
  const serverMsg = extractServerMessage(error);

  if (status === 403) {
    if (!serverMsg || isGenericAxiosStatusMessage(serverMsg)) {
      return DEFAULT_FORBIDDEN;
    }
    return serverMsg;
  }

  if (serverMsg) return serverMsg;
  const m = error?.message;
  if (m && !isGenericAxiosStatusMessage(m)) return m;
  return fallback;
}
